import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/api.service';
import { ThemingService } from 'src/app/theming.service';
import { AppComponent } from 'src/app/app.component';
import { LambdaService } from 'src/app/lambda.service';
import {
  isOptionType,
  isOptionValid,
  isValidHttpUrl,
  getOptionTypeLabel,
  createBlankOption,
  createBlankSubOption,
  toStringOptionType,
  toNumericOptionType,
} from '../shared/menu-form.helpers';

declare var $: any;

@Component({
  selector: 'app-edit-menu',
  templateUrl: './edit-menu.component.html',
  styleUrls: ['./edit-menu.component.scss']
})
export class EditMenuComponent implements OnInit {
  @Input() menuId!: number;
  @Output() close = new EventEmitter<void>();

  menu: any = { menuName: '', menuStatus: 0 };
  menuOptions: any[] = [];
  categories: any[] = [];
  collections: any[] = [];
  subCategories: any[] = [];
  subSubCategories: any[] = [];
  components: any[] = [];
  pages: any[] = [];
  loading = false;
  deletedMenuOptionIds: number[] = [];
  itemListId = '';

  // Stepper
  currentStep = 1;
  readonly steps = [
    { index: 1, label: 'MENU_STEP_2_TITLE', description: 'MENU_STEP_2_DESC' },
    { index: 2, label: 'MENU_STEP_3_TITLE', description: 'MENU_STEP_3_DESC' },
    { index: 3, label: 'MENU_STEP_4_TITLE', description: 'MENU_STEP_4_DESC' },
  ];
  selectedOptionIndex = -1;
  activeSubOptionIndex = -1;
  private shouldReturnToOptionsAfterSave = false;

  // Translation
  selectedLanguage: 'es' | 'en' = 'es';
  translatedContent: any = null;

  // Template-accessible helpers
  isOptionType = isOptionType;
  isValidHttpUrl = isValidHttpUrl;
  getOptionTypeLabel = getOptionTypeLabel;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private themingService: ThemingService,
    public appComponent: AppComponent,
    private cdr: ChangeDetectorRef,
    private lambdaService: LambdaService
  ) {
    this.route.paramMap.subscribe((params) => {
      let menuId = Number(params.get('menuId'));
      if (menuId !== this.menuId) {
        this.appComponent.goToTop();
        this.menuId = menuId;
      }
    });
  }

  ngOnInit(): void {
      this.cdr.detectChanges();
      this.getCategories(),
      this.getCollections(),
      this.getComponents(),
      this.getSubCategories(),
      this.getSubSubCategories(),
      this.getPages(),
      this.getMenuById(this.menuId);
  }

  async getMenuById(id: number): Promise<void> {
    if (!id) return;
    this.loading = true;

    try {
      const data: any = await this.lambdaService.getMenuById(id, this.themingService.shopId);
      if (data.menu?.length) this.menu = data.menu[0];

      // Parse translatedContent from API
      const rawTC = this.menu?.translatedContent;
      try {
        this.translatedContent = rawTC ? (typeof rawTC === 'string' ? JSON.parse(rawTC) : rawTC) : null;
      } catch {
        this.translatedContent = null;
      }
      this.menuOptions = this.buildMenuTree(this.resolveMenuOptionRows(data));
      this.applyTranslatedContentToMenuOptions(this.menuOptions);
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  private resolveMenuOptionRows(data: any): any[] {
    if (Array.isArray(this.menu?.menuOptions)) {
      return this.menu.menuOptions;
    }

    if (Array.isArray(data?.menuOptions)) {
      return data.menuOptions;
    }

    return Array.isArray(data?.menu_options) ? data.menu_options : [];
  }

  private buildMenuTree(rows: any[]): any[] {
    const map = new Map<number, any>();
    const roots: any[] = [];

    rows.forEach((row: any) => {
      const optionId = Number(row?.menuOptionId ?? row?.optionId ?? 0);
      const parentOptionId = row?.parentOptionId ?? null;
      const node: any = {
        optionType: toStringOptionType(row?.menuOptionType ?? row?.optionType),
        optionName: row?.menuOptionName ?? row?.optionName ?? '',
        optionNameEn: row?.optionNameEn ?? '',
        manualTranslation: row?.manualTranslation === true,
        optionId,
        categoryId: row?.categoryId ?? 0,
        subCategoryId: row?.subCategoryId ?? 0,
        subSubCategoryId: row?.subSubCategoryId ?? 0,
        collectionId: row?.collectionId ?? 0,
        optionUrl: row?.menuOptionUrl ?? row?.optionUrl ?? '',
        position: row?.menuOptionPosition ?? row?.position ?? 0,
        componentId: row?.componentId ?? 0,
        pageId: row?.pageId ?? 0,
        parentOptionId,
        children: [] as any[],
      };

      map.set(optionId, node);
    });

    rows.forEach((row: any) => {
      const optionId = Number(row?.menuOptionId ?? row?.optionId ?? 0);
      const parentOptionId = row?.parentOptionId ?? null;
      const node = map.get(optionId);

      if (!node) {
        return;
      }

      if (parentOptionId === null || parentOptionId === 0 || !map.has(parentOptionId)) {
        roots.push(node);
        return;
      }

      this.ensureChildrenArray(map.get(parentOptionId)).push(node);
    });

    const sortByPos = (items: any[]) => {
      items.sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
      items.forEach((item) => sortByPos(this.ensureChildrenArray(item)));
    };

    sortByPos(roots);
    return roots;
  }

  private applyTranslatedContentToMenuOptions(menuOptions: any[]): void {
    const translatedOptions = this.getTranslatedOptions();
    const translatedOptionMap = new Map<number, any>(
      translatedOptions
        .filter((option: any) => Number.isFinite(Number(option?.optionId)))
        .map((option: any) => [Number(option.optionId), option])
    );

    menuOptions.forEach((option: any) => {
      const translatedOption = translatedOptionMap.get(option.optionId);
      option.manualTranslation = translatedOption?.manual === true;
      option.optionNameEn = this.getNormalizedText(
        translatedOption?.optionName,
        option.optionNameEn
      );

      const translatedChildren = Array.isArray(translatedOption?.children)
        ? translatedOption.children
        : [];
      const translatedChildMap = new Map<number, any>(
        translatedChildren
          .filter((child: any) => Number.isFinite(Number(child?.subOptionId)))
          .map((child: any) => [Number(child.subOptionId), child])
      );

      this.ensureChildrenArray(option).forEach((child: any) => {
        const translatedChild = translatedChildMap.get(child.optionId);
        child.manualTranslation = translatedChild?.manual === true;
        child.optionNameEn = this.getNormalizedText(
          translatedChild?.subOptionName,
          child.optionNameEn
        );
      });
    });
  }

  private getTranslatedOptions(): any[] {
    return Array.isArray(this.translatedContent?.en?.options)
      ? this.translatedContent.en.options
      : [];
  }

  private ensureChildrenArray(item: any): any[] {
    if (!Array.isArray(item?.children)) {
      item.children = [];
    }

    return item.children;
  }

  private getNormalizedText(value: unknown, fallback = ''): string {
    if (typeof value === 'string') {
      return value;
    }

    return typeof fallback === 'string' ? fallback : '';
  }

  private getTrimmedText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  async getPages(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getPages(this.themingService.shopId);
      const pageRows = Array.isArray(response.data) ? response.data : [];
      this.pages = pageRows;
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      this.loading = false;
    }
  }

  async getCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCategories(this.themingService.shopId);
      const categoryRows = Array.isArray(response[0]) ? response[0] : [];
      this.categories = categoryRows;
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async getCollections(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCollections(this.themingService.shopId);
      this.collections = Array.isArray(response.collections) ? response.collections : [];
    } catch (error) {
      console.error('Error fetching collections:', error);
      this.collections = [];
    }
  }

  async getSubCategories(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getSubCategories(this.themingService.shopId);
      this.subCategories = Array.isArray(response.subCategories) ? response.subCategories : [];
    } catch (error) {
      console.error('Error fetching subCategories:', error);
    } finally {
      this.loading = false;
    }
  }

  async getSubSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubSubCategories(this.themingService.shopId);
      const subSubCategoryRows = Array.isArray(response.subSubCategories) ? response.subSubCategories : [];
      this.subSubCategories = subSubCategoryRows;
    } catch (error) {
      console.error('Error fetching subSubCategories:', error);
    }
  }

  async getComponents(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getComponents(this.themingService.shopId);
      const componentRows = Array.isArray(response[0]) ? response[0] : [];
      this.components = componentRows;
    } catch (error) {
      console.error('Error fetching components:', error);
    } finally {
      this.loading = false;
    }
  }

  // ─── Stepper ──────────────────────────────

  get stepProgress(): number {
    return (this.currentStep / this.steps.length) * 100;
  }

  resolveStep(targetStep: number): number {
    const boundedStep = Math.max(1, Math.min(targetStep, this.steps.length));

    if (boundedStep === 1) {
      return boundedStep;
    }

    if (this.menuOptions.length === 0) {
      return 1;
    }

    if (this.selectedOptionIndex < 0 || this.selectedOptionIndex >= this.menuOptions.length) {
      return 1;
    }

    return boundedStep;
  }

  nextStep(): void {
    this.currentStep = this.resolveStep(this.currentStep + 1);
  }

  prevStep(): void {
    this.currentStep = Math.max(this.currentStep - 1, 1);
  }

  goToStep(target: number): void {
    this.currentStep = this.resolveStep(target);
  }

  isCurrentStep(step: number): boolean { return this.currentStep === step; }

  isStepComplete(step: number): boolean {
    return step < this.currentStep && this.isStepValid(step);
  }

  isStepValid(step: number): boolean {
    switch (step) {
      case 1: return this.menuOptions.length > 0;
      case 2: {
        const opt = this.menuOptions[this.selectedOptionIndex];
        return opt ? isOptionValid(opt).valid : false;
      }
      case 3:
      default: return true;
    }
  }

  editOption(index: number): void {
    this.selectedOptionIndex = index;
    this.activeSubOptionIndex = -1;
    this.currentStep = this.resolveStep(2);
  }

  viewSuboptions(index: number): void {
    this.selectedOptionIndex = index;
    this.activeSubOptionIndex = -1;
    this.currentStep = this.resolveStep(3);
  }

  backToOptions(): void {
    this.selectedOptionIndex = -1;
    this.activeSubOptionIndex = -1;
    this.currentStep = 1;
  }

  backToOption(): void {
    this.activeSubOptionIndex = -1;
    this.currentStep = 2;
  }

  editSubOption(index: number): void {
    this.activeSubOptionIndex = index;
  }

  get selectedOption(): any {
    return this.menuOptions[this.selectedOptionIndex] ?? null;
  }

  get activeSubOption(): any {
    return this.selectedOption?.children?.[this.activeSubOptionIndex] ?? null;
  }

  trackByIndex(index: number): number { return index; }

  // ─── Option management ────────────────────

  addMenuOption(): void {
    this.menuOptions.push(createBlankOption(this.menuOptions.length + 1));
  }

  removeMenuOption(index: number): void {
    const removedSelectedOption = this.selectedOptionIndex === index;
    const optionId = this.menuOptions[index].optionId;
    if (optionId !== 0) {
      this.deletedMenuOptionIds.push(optionId);
    }
    this.menuOptions.splice(index, 1);
    this.menuOptions.forEach((opt, i) => opt.position = i + 1);

    if (removedSelectedOption) {
      this.backToOptions();
      return;
    }

    if (index < this.selectedOptionIndex) {
      this.selectedOptionIndex -= 1;
    }

    if (this.selectedOptionIndex >= this.menuOptions.length) {
      this.selectedOptionIndex = -1;
      this.activeSubOptionIndex = -1;
    }
  }

  onOptionChange(option: string, index: number): void {
    const target = this.menuOptions[index];
    target.categoryId = 0;
    target.subCategoryId = 0;
    target.subSubCategoryId = 0;
    target.collectionId = 0;
    target.optionUrl = '';
    target.componentId = 0;
    target.pageId = 0;
    target.optionType = option;
  }

  onSubOptionTypeChange(option: string): void {
    const child = this.activeSubOption;
    if (!child) return;
    child.categoryId = 0;
    child.subCategoryId = 0;
    child.subSubCategoryId = 0;
    child.collectionId = 0;
    child.optionUrl = '';
    child.componentId = 0;
    child.pageId = 0;
    child.optionType = option;
  }

  applySelectValue(target: any, field: string, event: any): void {
    target[field] = +event.target.value;
  }

  onCategoryChange(event: any, index: number) {
    const selectedCategory = this.categories.find(category => category.categoryId === +event.target.value);
    if (selectedCategory) {
      this.menuOptions[index].categoryId = selectedCategory.categoryId;
    }
  }

  onSubCategoryChange(event: any, index: number) {
    const selectedSubCategory = this.subCategories.find(subCategory => subCategory.subCategoryId === +event.target.value);
    if (selectedSubCategory) {
      this.menuOptions[index].subCategoryId = selectedSubCategory.subCategoryId;
    }
  }

  onSubSubCategoryChange(event: any, index: number) {
    const selectedSubSubCategory = this.subSubCategories.find(subSubCategory => subSubCategory.subSubCategoryId === +event.target.value);
    if (selectedSubSubCategory) {
      this.menuOptions[index].subSubCategoryId = selectedSubSubCategory.subSubCategoryId;
    }
  }

  onCollectionChange(event: any, index: number) {
    const selectedCollection = this.collections.find(collection => collection.collectionId === +event.target.value);
    if (selectedCollection) {
      this.menuOptions[index].collectionId = selectedCollection.collectionId;
    }
  }

  onComponentChange(event: any, index: number) {
    const selectedComponent = this.components.find(component => component.componentId === +event.target.value);
    if (selectedComponent) {
      this.menuOptions[index].componentId = selectedComponent.componentId;
      if (selectedComponent.categoryId) {
        this.menuOptions[index].categoryId = selectedComponent.categoryId;
      }
      if (selectedComponent.subCategoryId) {
        this.menuOptions[index].subCategoryId = selectedComponent.subCategoryId;
      }
      if (selectedComponent.subSubCategoryId) {
        this.menuOptions[index].subSubCategoryId = selectedComponent.subSubCategoryId;
      }
      if (selectedComponent.collectionId) {
        this.menuOptions[index].collectionId = selectedComponent.collectionId;
      }
    }
  }

  onPageChange(event: any, index: number) {
    const selectedPage = this.pages.find(page => page.pageId === +event.target.value);
    if (selectedPage) {
      this.menuOptions[index].pageId = selectedPage.pageId;
    }
  }

  onUrlChange(event: any, index: number) {
    this.menuOptions[index].optionUrl = event.target.value;
  }

  saveMenu(): void {
    this.shouldReturnToOptionsAfterSave = false;
    this.validateFormData();
  }

  saveAndBackFromOption(): void {
    this.shouldReturnToOptionsAfterSave = true;
    this.validateFormData();
  }

  validateFormData(): void {
    for (let i = 0; i < this.menuOptions.length; i++) {
      const result = isOptionValid(this.menuOptions[i]);
      if (!result.valid) {
        this.shouldReturnToOptionsAfterSave = false;
        this.selectedOptionIndex = i;
        this.activeSubOptionIndex = -1;
        this.currentStep = 2;
        if (result.errorType === 'url') {
          this.itemListId = `${i + 1}`;
          $('#errorEditUrl').modal('show');
        } else {
          $('#requiredEditMenuModal').modal('show');
        }
        return;
      }
      const children = this.ensureChildrenArray(this.menuOptions[i]);
      for (let j = 0; j < children.length; j++) {
        const childResult = isOptionValid(children[j]);
        if (!childResult.valid) {
          this.shouldReturnToOptionsAfterSave = false;
          this.selectedOptionIndex = i;
          this.activeSubOptionIndex = j;
          this.currentStep = 3;
          if (childResult.errorType === 'url') {
            this.itemListId = `S${i + 1}.${j + 1}`;
            $('#errorEditUrl').modal('show');
          } else {
            $('#requiredEditMenuModal').modal('show');
          }
          return;
        }
      }
    }
    this.updateMenu();
  }

  updateMenu() {
    this.loading = true;
    const flat: any[] = [];
    const walk = (node: any, parentId: number | null) => {
      flat.push({
        menuId          : this.menu.menuId,
        menuOptionId    : node.optionId,
        parentOptionId  : parentId !== null ? parentId : 0,
        menuOptionPosition: node.position,
        menuOptionName  : node.optionName,
        menuOptionUrl   : node.optionUrl,
        categoryId      : node.categoryId,
        subCategoryId   : node.subCategoryId,
        subSubCategoryId: node.subSubCategoryId,
        collectionId    : node.collectionId,
        menuOptionType  : toNumericOptionType(node.optionType),
        componentId     : node.componentId || 0,
        pageId          : node.pageId || 0
      });
      this.ensureChildrenArray(node).forEach((c: any) => walk(c, node.optionId));
    };
    this.menuOptions.forEach(root => walk(root, null));
    this.lambdaService.updateMenu(
      this.themingService.shopId,
      this.menu.menuId,
      this.menu.menuName,
      this.menu.menuStatus,
      flat,
      this.deletedMenuOptionIds,
      this.buildTranslatedContent()
    )
    .then((res: any) => {
      if (res.status === 200) {
        const shouldReturnToOptionsAfterSave = this.shouldReturnToOptionsAfterSave;
        this.shouldReturnToOptionsAfterSave = false;
        if (shouldReturnToOptionsAfterSave) {
          this.backToOptions();
        }
        $('#successMenuModal').modal('show');
        this.getMenuById(this.menu.menuId);
      } else {
        this.shouldReturnToOptionsAfterSave = false;
        $('#errorMenuModal').modal('show');
      }
      this.loading = false;
    })
    .catch(err => {
      console.error(err);
      this.shouldReturnToOptionsAfterSave = false;
      this.loading = false;
      $('#errorMenuModal').modal('show');
    });
  }

  selectLanguage(lang: 'es' | 'en'): void {
    this.selectedLanguage = lang;
  }

  onItemTranslationToggle(item: any, event: Event): void {
    const enabled = (event.target as HTMLInputElement).checked;
    item.manualTranslation = enabled;
    if (!enabled) {
      item.optionNameEn = '';
    }
  }

  getTranslatedOptionName(index: number): string {
    const optionId = this.menuOptions[index]?.optionId;
    const translatedOption = this.getTranslatedOptions().find((option: any) => option?.optionId === optionId);
    const translated = this.getNormalizedText(translatedOption?.optionName);

    if (translated) {
      return translated;
    }

    return '';
  }

  getTranslatedSubOptionName(optionIndex: number, subIndex: number): string {
    const optionId = this.menuOptions[optionIndex]?.optionId;
    const subOptionId = this.menuOptions[optionIndex]?.children?.[subIndex]?.optionId;
    const translatedOption = this.getTranslatedOptions().find((option: any) => option?.optionId === optionId);
    const translatedChild = Array.isArray(translatedOption?.children)
      ? translatedOption.children.find((child: any) => child?.subOptionId === subOptionId)
      : null;
    const translated = this.getNormalizedText(translatedChild?.subOptionName);

    if (translated) {
      return translated;
    }

    return '';
  }

  buildTranslatedContent(): any {
    const options = this.menuOptions.map((opt: any) => {
      const optionName = this.getTrimmedText(opt?.optionNameEn);
      const optionIsManual = opt?.manualTranslation === true && optionName.length > 0;
      const children = this.ensureChildrenArray(opt).map((child: any) => {
        const subOptionName = this.getTrimmedText(child?.optionNameEn);
        const childIsManual = child?.manualTranslation === true && subOptionName.length > 0;

        return {
          subOptionId: child?.optionId > 0 ? child.optionId : null,
          manual: childIsManual,
          subOptionName: childIsManual ? subOptionName : ''
        };
      });

      return {
        optionId: opt?.optionId > 0 ? opt.optionId : null,
        manual: optionIsManual,
        optionName: optionIsManual ? optionName : '',
        children
      };
    });

    return { en: { options } };
  }

  onSubUrlChange(event: any, parentIndex: number, childIndex: number): void {
    this.menuOptions[parentIndex]
        .children[childIndex]
        .optionUrl = event.target.value;
  }

  addSubMenuOption(parentIndex: number): void {
    const parent = this.menuOptions[parentIndex];
    const children = this.ensureChildrenArray(parent);
    children.push(createBlankSubOption(children.length + 1));
  }

  removeSubMenuOption(parentIndex: number, childIndex: number): void {
    const children = this.ensureChildrenArray(this.menuOptions[parentIndex]);
    const optionId = children[childIndex].optionId;
    if (optionId !== 0) {
      this.deletedMenuOptionIds.push(optionId);
    }
    children.splice(childIndex, 1);
    children.forEach((c: any, idx: number) => (c.position = idx + 1));

    if (this.selectedOptionIndex === parentIndex) {
      if (this.activeSubOptionIndex === childIndex) {
        this.activeSubOptionIndex = children.length ? Math.min(childIndex, children.length - 1) : -1;
      } else if (childIndex < this.activeSubOptionIndex) {
        this.activeSubOptionIndex -= 1;
      }
    }
  }

  closeModal(): void {
    $('#successMenuModal').modal('hide');
    $('#errorMenuModal').modal('hide');
    $('#requiredEditMenuModal').modal('hide');
    $('#errorEditUrl').modal('hide');
  }

  closeEdit(): void {
    this.close.emit();
  }
}
